import classNames from 'classnames';
import type { ComponentProps } from 'react';
import React, { useEffect, useId, useLayoutEffect, useRef } from 'react';
import { getOverflowAncestors } from '@floating-ui/react-dom';
import { isTouchDevice } from '../constants/os';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import useOpenManager from '../utils/popover/useOpenManager';
import useTriggerElement from '../utils/popover/useTriggerElement';
import type { HoverDelay } from '../utils/useHover';
import useHover from '../utils/useHover';
import ErrorBoundary from './ErrorBoundary';
import * as styles from './HoverView.module.scss';
import Slot from './Slot';
import type { TetherRef } from './Tether';
import Tether from './Tether';

const interactiveElementSelector =
  'button:not([disabled]), a[href], [role="button"], [tabindex]';

type HoverViewProps = Pick<
  ComponentProps<typeof Tether>,
  'placement' | 'flipBehavior' | 'offset'
> & {
  anchorElement?: HTMLElement | null;
  children: React.ReactNode;
  hoverGroupId?: string;
  hoverDelay?: HoverDelay;
  view: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * A headless component that displays a view when hovering over its children.
 * Both the view and the anchor (children) must be able to accept props and pass them
 * down to their underlying HTML elements, since they will receive refs, event handlers, etc.
 *
 * Following the product spec, when the anchor is not interactive, the view also appears
 * when the anchor is clicked.
 *
 * @example
 * <HoverView view={<FloatingCard>Hello</FloatingCard>} placement="top">
 *   <div>Hover over me</div>
 * </HoverView>
 */
const HoverView = ({
  anchorElement: providedAnchorElement,
  children,
  hoverGroupId,
  hoverDelay = 'slow',
  view,
  open,
  onOpenChange,
  placement,
  flipBehavior,
  offset,
}: HoverViewProps) => {
  const id = useId();

  const { isOpen, hasBeenOpened, onIsOpenChange } = useOpenManager({
    open,
    onOpenChange,
  });

  const tetherRef = useRef<TetherRef>(null);
  useLayoutEffect(() => {
    if (isOpen) tetherRef.current?.updatePositionedView();
  }, [isOpen]);

  const {
    onMouseEnter: originalOnMouseEnter,
    onMouseLeave: originalOnMouseLeave,
    setHover,
  } = useHover({
    hoverGroupId,
    hoverDelay,
    onHoverChange(hoveringId) {
      onIsOpenChange(hoveringId === id);
    },
  });

  useEffect(() => {
    setHover(isOpen ? id : (current) => (current === id ? null : current));
  }, [setHover, isOpen, id]);

  const onMouseEnter = isTouchDevice
    ? undefined
    : (e: React.MouseEvent<HTMLElement>) => {
        originalOnMouseEnter(id, e);
      };

  const onMouseLeave = isTouchDevice
    ? undefined
    : () => {
        originalOnMouseLeave(id);
      };

  const [triggerElement, setTriggerElement] = useTriggerElement();

  const onClickOutside = (e: Event) => {
    if (!(e.target instanceof Node) || !triggerElement?.contains(e.target)) {
      setHover(null);
    }
  };

  const anchorElement = providedAnchorElement ?? triggerElement;
  useEffect(() => {
    if (!isOpen || !anchorElement) return undefined;

    const handleScroll = () => setHover(null);

    const ancestors = getOverflowAncestors(anchorElement);
    ancestors.forEach((ancestor) => {
      ancestor.addEventListener('scroll', handleScroll, { passive: true });
    });

    return () => {
      ancestors.forEach((ancestor) => {
        ancestor.removeEventListener('scroll', handleScroll);
      });
    };
  }, [isOpen, anchorElement, setHover]);

  return (
    <>
      <Slot
        ref={setTriggerElement}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={() => {
          if (!triggerElement) return;

          const isInteractive =
            !!triggerElement.querySelector(interactiveElementSelector) ||
            !!triggerElement.closest(interactiveElementSelector);

          // Only open the hover view if the element is not interactive,
          // to avoid conflicting with the element's default behavior
          if (!isInteractive) setHover(id);
        }}
      >
        {children}
      </Slot>
      <ErrorBoundary name="HoverView">
        <Tether
          ref={tetherRef}
          anchorElement={anchorElement}
          positionedView={
            hasBeenOpened && (
              <ClickOutsideHandler
                asChild
                onClickOutside={isOpen ? onClickOutside : undefined}
              >
                <Slot
                  aria-hidden={!isOpen}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  className={classNames(styles.floating, {
                    [styles.isClosed]: !isOpen,
                  })}
                >
                  {view}
                </Slot>
              </ClickOutsideHandler>
            )
          }
          flipBehavior={flipBehavior}
          placement={placement}
          offset={offset}
        />
      </ErrorBoundary>
    </>
  );
};

export default HoverView;
